import { useState } from "react";
import { ArrowRight, ArrowDown, Check, Loader2, MapPin, Users, Wrench } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useDocumentMeta } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";

const TERRECUITE = "#8A5541";

const ADVANTAGES = [
  { icon: Users, title: "Des chantiers suivis et cadrés", text: "Intervenez sur des projets CozyHome préparés en amont, avec des informations claires pour chaque mission." },
  { icon: Wrench, title: "Un cadre de travail simple", text: "Suivi d'avancement, observations chantier, devis et factures centralisés au même endroit." },
  { icon: MapPin, title: "Votre indépendance préservée", text: "Votre entreprise reste indépendante : vous collaborez sur les chantiers qui vous sont confiés." },
];

const STEPS = [
  ["Candidature", "Vous présentez votre entreprise et votre corps de métier."],
  ["Étude du dossier", "CozyHome étudie votre profil et vos disponibilités."],
  ["Validation", "Votre entreprise est référencée dans le réseau CozyHome."],
  ["Premiers chantiers", "Vous êtes affecté aux chantiers correspondant à votre métier et votre secteur."],
];

const EMPTY_FORM = {
  firstname: "", lastname: "", phone: "", email: "",
  company_name: "", siret: "", trade: "",
  city: "", postal_code: "", department: "",
  message: "", consent: false,
};

const inputClass =
  "w-full rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-3.5 text-base font-semibold text-navy placeholder:font-normal placeholder:text-stone-400 focus:border-[#8A5541] focus:outline-none";

export default function CompanyApplicationPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useDocumentMeta(
    "Rejoindre le réseau d'entreprises CozyHome — Candidature | CozyHome",
    "Votre entreprise ou activité d'artisan souhaite rejoindre le réseau CozyHome ? Déposez votre candidature en quelques minutes."
  );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const scrollToForm = () => {
    document.getElementById("candidature-entreprise")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Merci de renseigner vos coordonnées.");
      return;
    }
    if (!form.company_name.trim() || !form.trade.trim()) {
      toast.error("Merci de renseigner le nom de votre entreprise et votre corps de métier.");
      return;
    }
    if (!form.city.trim() || !form.postal_code.trim() || !form.department.trim()) {
      toast.error("Merci de renseigner votre localisation.");
      return;
    }
    if (!/^\d{5}$/.test(form.postal_code.trim())) {
      toast.error("Le code postal doit contenir 5 chiffres.");
      return;
    }
    if (!form.consent) {
      toast.error("Merci d'accepter le traitement de votre candidature.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/company-applications", {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        company_name: form.company_name.trim(),
        siret: form.siret.trim(),
        trade: form.trade.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        department: form.department.trim(),
        message: form.message.trim(),
        consent: form.consent,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* ---------------- Hero ---------------- */}
      <section data-testid="page-entreprise-candidature-hero" className="bg-ivory pb-16 pt-32 sm:pb-20 sm:pt-36">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] sm:text-sm" style={{ color: TERRECUITE }}>
            Rejoindre le réseau d'entreprises
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.08] tracking-tight text-navy sm:text-6xl">
            Votre entreprise <span className="italic" style={{ color: TERRECUITE }}>au cœur des chantiers CozyHome.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
            Artisan ou dirigeant d'une entreprise du bâtiment, rejoignez le réseau CozyEntreprise et accédez à des
            chantiers suivis et cadrés, tout en restant indépendant.
          </p>
          <button
            data-testid="entreprise-candidature-hero-cta"
            onClick={scrollToForm}
            className="group mt-8 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90"
            style={{ backgroundColor: TERRECUITE }}
          >
            Candidater pour mon entreprise
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
        </div>
      </section>

      <section className="bg-ivory pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl space-y-10 px-5 sm:px-8">
          {/* ---------------- Avantages ---------------- */}
          <Reveal>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: TERRECUITE }}>Ce que vous apporte le réseau</p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Trois avantages concrets</h2>
              <div data-testid="entreprise-candidature-avantages" className="mt-8 grid gap-4 sm:grid-cols-3">
                {ADVANTAGES.map((a, i) => (
                  <div key={a.title} data-testid={`entreprise-avantage-${i}`} className="rounded-3xl bg-white p-7">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: `${TERRECUITE}14`, color: TERRECUITE }}>
                      <a.icon className="h-4.5 w-4.5" />
                    </span>
                    <p className="mt-4 text-sm font-bold text-navy">{i + 1} — {a.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-stone-500">{a.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* ---------------- Comment ça fonctionne ---------------- */}
          <Reveal>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: TERRECUITE }}>Le parcours</p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Comment ça fonctionne ?</h2>
              <ol data-testid="entreprise-candidature-etapes" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {STEPS.map(([t, d], i) => (
                  <li key={t} className="rounded-3xl bg-white p-6">
                    <span className="font-display text-2xl font-medium" style={{ color: TERRECUITE }}>{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-3 text-sm font-bold text-navy">{t}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-stone-500">{d}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          {/* ---------------- Formulaire ---------------- */}
          <Reveal>
            <article id="candidature-entreprise" data-testid="entreprise-candidature-form-section" className="scroll-mt-28 overflow-hidden rounded-3xl bg-white">
              <div className="h-1.5 w-full" style={{ backgroundColor: TERRECUITE }} aria-hidden="true" />
              <div className="p-7 sm:p-12">
                {submitted ? (
                  <div data-testid="entreprise-candidature-success" className="py-8 text-center">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${TERRECUITE}14`, color: TERRECUITE }}>
                      <Check className="h-7 w-7" />
                    </span>
                    <h2 className="mt-6 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Merci pour votre candidature</h2>
                    <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                      Votre demande a bien été transmise à CozyHome. Notre équipe va étudier le profil de votre
                      entreprise et vous recontacter pour la suite.
                    </p>
                    <p className="mx-auto mt-3 max-w-xl text-sm font-semibold text-stone-500">
                      Aucun document ni paiement n'est nécessaire à ce stade.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: TERRECUITE }}>Candidature</p>
                    <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Présentez votre entreprise</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                      Ce formulaire ne demande aucun document. Les justificatifs ne seront demandés qu'après une
                      première étude de votre profil.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Vos coordonnées</p>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <input data-testid="entreprise-candidature-firstname" required value={form.firstname} onChange={(e) => set("firstname", e.target.value)}
                            placeholder="Prénom *" autoComplete="given-name" className={inputClass} />
                          <input data-testid="entreprise-candidature-lastname" required value={form.lastname} onChange={(e) => set("lastname", e.target.value)}
                            placeholder="Nom *" autoComplete="family-name" className={inputClass} />
                          <input data-testid="entreprise-candidature-phone" required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                            placeholder="Téléphone *" autoComplete="tel" className={inputClass} />
                          <input data-testid="entreprise-candidature-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                            placeholder="E-mail *" autoComplete="email" className={inputClass} />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Votre entreprise</p>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <input data-testid="entreprise-candidature-company-name" required value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                            placeholder="Nom de l'entreprise *" className={inputClass} />
                          <input data-testid="entreprise-candidature-siret" value={form.siret} onChange={(e) => set("siret", e.target.value)}
                            placeholder="SIRET (facultatif)" className={inputClass} />
                          <input data-testid="entreprise-candidature-trade" required value={form.trade} onChange={(e) => set("trade", e.target.value)}
                            placeholder="Corps de métier *" className={`${inputClass} sm:col-span-2`} />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Localisation</p>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <input data-testid="entreprise-candidature-city" required value={form.city} onChange={(e) => set("city", e.target.value)}
                            placeholder="Ville *" className={inputClass} />
                          <input data-testid="entreprise-candidature-postal-code" required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                            placeholder="Code postal *" inputMode="numeric" className={inputClass} />
                          <input data-testid="entreprise-candidature-department" required value={form.department} onChange={(e) => set("department", e.target.value)}
                            placeholder="Département *" className={`${inputClass} sm:col-span-2`} />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Parlez-nous de votre entreprise</p>
                        <textarea
                          data-testid="entreprise-candidature-message"
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          placeholder="Présentez-nous en quelques mots votre entreprise, votre zone d'intervention et vos disponibilités."
                          rows={5}
                          className={`${inputClass} resize-none`}
                        />
                      </div>

                      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4">
                        <input data-testid="entreprise-candidature-consent" type="checkbox" checked={form.consent}
                          onChange={(e) => set("consent", e.target.checked)}
                          className="mt-0.5 h-5 w-5 shrink-0 accent-[#8A5541]" />
                        <span className="text-sm leading-relaxed text-navy/80">
                          J'accepte que les informations transmises soient utilisées par CozyHome pour traiter ma
                          candidature, conformément à la politique de confidentialité. <span style={{ color: TERRECUITE }}>*</span>
                        </span>
                      </label>

                      <button
                        data-testid="entreprise-candidature-submit"
                        type="submit"
                        disabled={submitting}
                        className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-60 sm:w-auto"
                        style={{ backgroundColor: TERRECUITE }}
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                        Envoyer ma candidature
                      </button>
                    </form>
                  </>
                )}
              </div>
            </article>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
